import React, { useState } from 'react';
import axios from 'axios';
import { Spinner } from 'reactstrap';
import config from '../../config';
import { renderMarkdownNotes } from './callSummaryMarkdown';
import styles from '../../pages/customers/CustomerDetailsModal.module.scss';

function InfoRow({ label, value, renderValue, children }) {
  return (
    <tr>
      <td className={styles.infoLabel}>{label}</td>
      <td className={styles.infoValue}>
        {children || (renderValue ? renderValue() : (value || 'N/A'))}
      </td>
    </tr>
  );
}

export default function OperatorCallLogItem({ log, customerId }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryText, setSummaryText] = useState(log.conversation_summary || '');
  const [summaryNotes, setSummaryNotes] = useState(log.conversation_transcript || '');
  const [summaryError, setSummaryError] = useState('');

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      return new Date(isoString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return isoString;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getStatusInfo = (status) => {
    const map = {
      completed: { label: 'Accepted', color: '#16a34a', bg: '#dcfce7' },
      connected: { label: 'Connected', color: '#0284c7', bg: '#e0f2fe' },
      initiated: { label: 'Initiated', color: '#d97706', bg: '#fef3c7' },
      dialing: { label: 'Dialing', color: '#d97706', bg: '#fef3c7' },
      failed: { label: 'Failed', color: '#dc2626', bg: '#fee2e2' },
      rejected: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2' },
      no_answer: { label: 'No Answer', color: '#dc2626', bg: '#fee2e2' },
    };
    return map[status] || { label: status || 'Unknown', color: '#6b7280', bg: '#f3f4f6' };
  };

  const getCallTypeInfo = (callType) => {
    if (callType === 'incoming') {
      return { label: 'Incoming', icon: 'fa-phone', color: '#0284c7', bg: '#e0f2fe' };
    }
    return { label: 'Outgoing', icon: 'fa-phone', color: '#7c3aed', bg: '#ede9fe' };
  };

  const getSourceLabel = (source) => {
    if (source === 'phone_sync') return 'Phone Sync';
    return 'Dashboard';
  };

  const handleSummarizeConversation = async () => {
    if (!log?.id || !customerId) return;
    if (!log.recording_url) {
      setSummaryError('No recording available for this call.');
      return;
    }
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const response = await axios.post(
        `${config.customerApiBase}/customers/${customerId}/call-logs/${log.id}/summarize`,
        { force: false },
        { timeout: 180000 },
      );
      setSummaryText(response.data?.summary || '');
      setSummaryNotes(response.data?.transcript || '');
    } catch (error) {
      setSummaryError(error.response?.data?.detail || error.message);
    } finally {
      setSummaryLoading(false);
    }
  };

  const statusInfo = getStatusInfo(log.status);
  const callTypeInfo = getCallTypeInfo(log.call_type);
  const phone = log.customer_phone;

  return (
    <li className={styles.operatorCallItem}>
      <div className={styles.operatorCallHeader} onClick={() => setIsExpanded(!isExpanded)}>
        <div className={styles.operatorCallMain}>
          <span
            className={styles.operatorCallStatus}
            style={{ color: statusInfo.color, background: statusInfo.bg }}
          >
            {statusInfo.label}
          </span>
          <span
            className={styles.operatorCallTypeBadge}
            style={{ color: callTypeInfo.color, background: callTypeInfo.bg }}
          >
            <i className={`fa ${callTypeInfo.icon}`} />
            {callTypeInfo.label}
          </span>
          {log.sim_number ? (
            <span className={styles.operatorCallSim}>SIM: {log.sim_number}</span>
          ) : null}
          {log.has_conversation_summary ? (
            <span style={{ color: '#16a34a', fontSize: '0.7rem', fontWeight: 600 }} title="Summary available">
              <i className="fa fa-check-circle" /> Summary
            </span>
          ) : null}
        </div>
        <div className={styles.operatorCallMeta}>
          {log.recording_url ? (
            <span className={styles.operatorCallRecording} title="Recording available">
              <i className="fa fa-microphone" />
            </span>
          ) : null}
          <span className={styles.operatorCallDuration}>
            <i className="fa fa-clock-o" /> {formatDuration(log.duration_seconds)}
          </span>
          <span className={styles.operatorCallDate}>
            {formatDateTime(log.started_at || log.created_at)}
          </span>
          <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'} ${styles.operatorCallExpand}`} />
        </div>
      </div>
      {isExpanded ? (
        <div className={styles.operatorCallDetails}>
          <table className={styles.infoTable}>
            <tbody>
              <InfoRow label="Call ID" value={`#${log.id}`} />
              <InfoRow
                label="Direction"
                renderValue={() => (
                  <span style={{ color: callTypeInfo.color, fontWeight: 600 }}>
                    <i className={`fa ${callTypeInfo.icon}`} style={{ marginRight: '0.35rem' }} />
                    {callTypeInfo.label}
                  </span>
                )}
              />
              {phone ? <InfoRow label="Phone" value={phone} /> : null}
              {log.operator_id ? <InfoRow label="Operator" value={`User #${log.operator_id}`} /> : null}
              {log.sim_number ? <InfoRow label="SIM Number" value={log.sim_number} /> : null}
              {log.sim_role ? <InfoRow label="SIM Role" value={log.sim_role} /> : null}
              <InfoRow label="Source" value={getSourceLabel(log.source)} />
              <InfoRow
                label="Status"
                renderValue={() => (
                  <span style={{ color: statusInfo.color, fontWeight: 600 }}>{statusInfo.label}</span>
                )}
              />
              <InfoRow label="Duration" value={formatDuration(log.duration_seconds)} />
              <InfoRow label="Started" value={formatDateTime(log.started_at)} />
              <InfoRow label="Ended" value={formatDateTime(log.ended_at)} />
              <InfoRow label="Created" value={formatDateTime(log.created_at)} />
            </tbody>
          </table>
          {log.recording_url ? (
            <div className={styles.operatorCallRecordingSection}>
              <div className={styles.recordingLabel}>
                <i className="fa fa-headphones" /> Call Recording
              </div>
              <audio controls src={log.recording_url} className={styles.recordingPlayer}>
                <track kind="captions" />
              </audio>
            </div>
          ) : null}
          {log.recording_url ? (
            <div style={{ marginTop: '0.75rem' }}>
              <button
                type="button"
                style={{
                  background: summaryLoading ? '#94a3b8' : '#2563eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '6px 14px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: summaryLoading ? 'not-allowed' : 'pointer',
                }}
                disabled={summaryLoading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSummarizeConversation();
                }}
              >
                {summaryLoading ? (
                  <>
                    <Spinner size="sm" /> Listening to recording & summarizing...
                  </>
                ) : (
                  <>
                    <i className="fa fa-align-left" /> {summaryText ? 'Regenerate Summary' : 'Summarize Conversation'}
                  </>
                )}
              </button>
            </div>
          ) : null}
          {summaryError ? (
            <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.5rem' }}>{summaryError}</div>
          ) : null}
          {summaryText ? (
            <div
              style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: '0.85rem',
                lineHeight: 1.5,
              }}
            >
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: 4, fontWeight: 600 }}>
                Summary
              </div>
              {summaryText}
            </div>
          ) : null}
          {summaryNotes && summaryNotes !== summaryText ? (
            <div
              style={{
                marginTop: '0.5rem',
                padding: '0.75rem',
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: '0.78rem',
                maxHeight: 280,
                overflowY: 'auto',
              }}
            >
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: 4, fontWeight: 600 }}>
                Ops / Customer notes (from audio)
              </div>
              {renderMarkdownNotes(summaryNotes)}
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
